import type { Mutable, Unfurl, ValueOfType } from '@shencom/typing';
import { IsObject } from '../../judge/src';

type Nullable<T> = T | null | undefined;

type Arrayable<T> = T | Array<T>;
type Dictionary<T = any> = Record<string, T>;

/** 转数组
 *
 * @template T
 * @param {T|T[]} val
 * @returns {T[]}
 */
export function ToArray<T>(val?: Nullable<Arrayable<T>>): Array<T> {
  val = val ?? [];
  return Array.isArray(val) ? val : [val];
}

/** 数组转对象
 *
 * @template T
 * @param {T[]} list 数据列表
 * @param {string} key 指定使用字段作为 key
 * @example
 * ```ts
 * arr.ArrayToObject([], ''); // {}
 *
 * const data = [
 *   { id: 1, b: 11 },
 *   { id: 4, b: 44, c: 444 },
 * ];
 * arr.ArrayToObject(data, 'id');
 * ```
 * @returns {Record<string, T[]>} result
 */
export function ArrayToObject<T extends object>(list: T[], key: keyof T): Record<string, T[]> {
  const result = list.reduce<Record<string, T[]>>((pre, cur: any) => {
    if (pre[cur[key]]) pre[cur[key]].push(cur);
    else pre[cur[key]] = [cur];
    return pre;
  }, Object.create(null));
  return result;
}

interface ArrayToTreeMaps<T> {
  id?: string;
  pid?: string;
  children?: T;
}

/**
 * 数组转树
 *
 * @template T
 * @template C
 * @param {T} array - 数组数据
 * @param {ArrayToTreeMaps<C>} [maps={}] - 映射关系
 * @example
 * ```
 * const arr1 = [
 *   { id: '1', pid: '0' },
 *   { id: '1-1', pid: '1' },
 *   { id: '1-2', pid: '1' },
 *   { id: '1-1-1', pid: '1-1' },
 * ];
 * const res = ArrayToTree(arr1);
 * res = [
 *  {
 *    id: '1',
 *    pid: '0',
 *    children: [
 *      {
 *        id: '1-1',
 *        pid: '1',
 *        children: [{ id: '1-1-1', pid: '1-1' }],
 *       },
 *       { id: '1-2', pid: '1' },
 *     ],
 *   },
 * ];
 * ```
 * @return {Tree<T>}
 */
export function ArrayToTree<T extends any[], C extends string = 'children'>(
  array: T,
  maps: ArrayToTreeMaps<C> = {},
) {
  const id = maps?.id || 'id';
  const pid = maps?.pid || 'pid';
  const children = maps?.children || 'children';

  const tree: Dictionary[] = [];
  const nodeMap: Dictionary<Dictionary> = {};
  array.forEach((item) => {
    nodeMap[item[id]] = { ...item };
  });

  array.forEach((item) => {
    const node = nodeMap[item[id]];
    const parent = nodeMap[item[pid]];
    if (parent) {
      if (!parent[children]) parent[children] = [];
      parent[children].push(node);
    } else {
      tree.push(node);
    }
  });

  type Tree<P extends any[]> = (P[number] & { [K in C]?: Tree<P> })[];

  return tree as Tree<T>;
}

/**
 * 扁平化数组
 *
 * @export
 * @template T
 * @param {(Nullable<Arrayable<T | Array<T>>>)} [array] - 多维数组
 * @param {number} [depth=1] - 最大递归深度
 * @example
 * ```
 * ArrayFlatten([]); // []
 * ArrayFlatten([1]); // [1]
 * ArrayFlatten([1, [2]]); // [1, 2]
 * ArrayFlatten([1, [2], [3]]); // [1, 2, 3]
 * ArrayFlatten([1, [2, [3]]]); // [1, 2, [3]]
 * ArrayFlatten([1, [2, [3]]], 2); // [1, 2, 3]
 * ```
 * @return {*} {Array<T>}
 */
export function ArrayFlatten<T>(array?: Nullable<Arrayable<T | Array<T>>>, depth = 1): Array<T> {
  return ToArray(array).flat(depth) as Array<T>;
}

/**
 * 对数组中每一项的进行映射
 *
 * @template T
 * @template P
 * @param {T} array - 数组
 * @param {P} maps - 映射关系
 * @example
 * ```
 * const arr = [
 *  { a: '1', b: true, c: 3 },
 *  { a: '2', b: false, c: 4 },
 * ];
 * const res = ArrayKeyMap(arr, { aa: 'a', bb: 'b' } as const);
 * res = [
 *  { aa: '1', bb: true, c: 3 },
 *  { aa: '2', bb: false, c: 4 },
 * ];
 * ```
 * @return {*} {Array<T>}
 */
export function ArrayKeyMap<T extends Dictionary[], P extends Dictionary<string>>(
  array: T,
  maps: P,
) {
  type MapToValue<T extends {}, M extends {}> = {
    [K in keyof T]: ValueOfType<M, T[K] extends keyof M ? T[K] : any>;
  };

  type TItem = T[number];
  type Difference = Exclude<keyof TItem, P[keyof P]>; // c

  type Res = MapToValue<P, TItem> &
    (Difference extends never
      ? Dictionary & MapToValue<P, TItem>
      : {
          [K in Difference]: T[number][K];
        });

  return ToArray(array).map((item) => {
    if (!IsObject(item)) return item;

    const newItem = { ...item };
    Object.entries(maps).forEach(([key, value]) => {
      newItem[key] = item[value];
      delete newItem[value];
    });

    return newItem;
  }) as Unfurl<Mutable<Res>>[];
}
